import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { Button } from 'components/ui/Button/Button'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { NoData } from 'components/NoData'
import { Input } from 'components/Input/Input'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { setKpiTemplate, setSortOrder } from 'features/operations/deptOpsProfilesSlice'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { IDeptOpsProfileRow } from 'types/deptOpsConfig.interface'
import {
  DeptKey,
  InfoNote,
  OrderInputWrap,
  PageTitle,
  SubTitle,
  TemplateSelect,
} from './DepartmentOpsConfig.styled'

const KPI_TEMPLATES: IOpsDepartment['kpiTemplate'][] = [
  'mining',
  'plant',
  'maintenance',
  'camp',
  'security',
  'custom',
]

export const DepartmentOpsConfig = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { profiles } = useSelector((state: RootState) => state.deptOpsProfiles)

  const rows: IDeptOpsProfileRow[] = profiles.map((p) => ({ ...p, id: p.key }))

  const columns: IHonestTableColumn<IDeptOpsProfileRow>[] = [
    {
      id: 'department',
      header: 'Department',
      minWidth: '14rem',
      cell: (item) => (
        <Cell>
          <BodyText weight='600'>{item.name}</BodyText>
          <DeptKey>{item.key}</DeptKey>
        </Cell>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      minWidth: '10rem',
      cell: (item) => (
        <OtherText grey>
          {item.hrDepartmentId != null ? `HR registry · #${item.hrDepartmentId}` : 'demo seed'}
        </OtherText>
      ),
    },
    {
      id: 'kpiTemplate',
      header: 'KPI template',
      minWidth: '10rem',
      cell: (item) => (
        <TemplateSelect
          value={item.kpiTemplate}
          onChange={(e) => {
            const kpiTemplate = e.target.value as IOpsDepartment['kpiTemplate']
            dispatch(setKpiTemplate({ key: item.key, kpiTemplate }))
            toast.success(`Saved — ${item.name} now uses the ${kpiTemplate} view`)
          }}
        >
          {KPI_TEMPLATES.map((template) => (
            <option key={template} value={template}>
              {template}
            </option>
          ))}
        </TemplateSelect>
      ),
    },
    {
      id: 'order',
      header: 'Order',
      minWidth: '5rem',
      cell: (item) => (
        <OrderInputWrap>
          <Input
            type='number'
            value={item.sortOrder}
            allowZero
            nomarg
            noinvalidtext
            onChange={(e) =>
              dispatch(setSortOrder({ key: item.key, sortOrder: Number(e.target.value) || 0 }))
            }
          />
        </OrderInputWrap>
      ),
    },
    {
      id: 'preview',
      header: 'Preview',
      align: 'right',
      cell: (item) => (
        <Button variant='black' size='sm' onClick={() => navigate(`/operations/d/${item.key}`)}>
          Open Today view
        </Button>
      ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='8'>
          <Flex center gap='1rem'>
            <PageTitle>Department operations</PageTitle>
            <DemoBadge />
          </Flex>
          <Button variant='secondary' onClick={() => navigate('/hr/departments')}>
            Manage departments in HR
          </Button>
        </Flex>
        <SubTitle>
          Departments come from the HR registry — configure how each one appears in Operations.
        </SubTitle>
        {rows.length ? (
          <>
            <HonestTable columns={columns} data={rows} keyField='id' />
            <InfoNote>
              <OtherText grey>
                Every HR department appears in Operations. A &quot;uses Operations&quot; switch is
                planned on the HR department itself.
              </OtherText>
            </InfoNote>
          </>
        ) : (
          <NoData text='Departments will appear here once the HR registry is connected.' />
        )}
      </PageWrapper>
    </>
  )
}
